/**
 * Util file created for having common functions which will be used across the repo.
 */
import React from 'react';
import { ButtonRole, ButtonSubTheme, MinecraftTheme } from '@minecraftweb/web-app-ux';
import { Cv } from '@microsoft/1ds-cv-js';
import { constants, key, localStorageList, cookiesList, toastIcons, flights } from '../Constants/Constants';
import * as Cookies from 'es-cookie';
import { parseJwt, session } from '../@mojang/auth';
import { locale, sharedEnvConfig } from '../environments/common';
import { environment } from '../environment';
import { javaEntitlementKey } from '../Pages/Wallet/Constants/Constants';
import { ApiEntitlementItem, ApiEntitlementResponse } from './Interfaces/Utils.interface';
import { TRIAL_REALM } from '../Pages/OneStoreWallet/Constants/Constants';
import { RealmStatus } from '../Pages/OneStoreWallet/Interfaces/OneStoreWallet.interface';
import { ButtonColor } from '../CoreUI/Buttons/Interfaces/Buttons.interface';
import { NetService, NetServiceTypes } from 'minecraft-net-core-services';
import { IPurchaseEventHandlers } from '@purchase-platform/sdk/dist/sdk/src/purchase/buynow/types';
import { IPurchasePostMessage } from '@purchase-platform/sdk/dist/models/src';

/**
 * daysDifference function is used to calculate the days count between the two given date range.
 *
 * @param fromDate number
 * @param currentDate number
 * @returns number
 */
export function daysDifference(fromDate: number, currentDate: number) {
  return Math.round((currentDate - fromDate) / (1000 * 60 * 60 * 24));
}

/**
 * daysDifference function is used to calculate the hours count between the two given date range.
 *
 * @param fromDate number
 * @param currentDate number
 * @returns number
 */
export function hoursDifference(fromDate: number, currentDate: number) {
  return Math.ceil((30 - (currentDate - fromDate) / (1000 * 60 * 60 * 24)) * 24);
}

/**
 * This function will check route path.
 * If the route path is null or undefined. Then we will share the default route path.
 * @param route String
 * @returns string
 */
export function safePath(route: string) {
  return route || '/';
}

/**
 * This function will format price.
 * Will return the value in local currency.
 * @param amount: number
 * @param currency: string
 * @param locale: string
 * @returns string
 */
export function formatPrice(amount: number, currency: string, locale: string) {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}

/**
 * This function will make the focus available on the given ref element.
 * @param refValue
 * @returns
 */
export function refFocus(refValue: React.RefObject<HTMLElement>) {
  return refValue.current?.focus();
}
/**
 * This function will create the focus trap for the given element.
 * If user enter tab from the last focusable element then it should move to the first.
 * If user enter shift tab from the first focusable element, then focus moved to the last.
 * If user enter esc key. Then the overlay should be closed.
 * @param event
 * @param refFirstFocusable
 * @param refLastFocusable
 * @param setEscKeyPressed
 * @param showOverlay
 */
export function tabTrap(
  event: React.KeyboardEvent,
  refFirstFocusable: React.RefObject<HTMLElement>,
  refLastFocusable: React.RefObject<HTMLElement>,
  setEscKeyPressed: Function,
  showOverlay: Function
) {
  if (document.activeElement === refLastFocusable.current && event.key === key.tab && !event.shiftKey) {
    event.preventDefault();
    refFocus(refFirstFocusable);
  }
  if (document.activeElement === refFirstFocusable.current && event.key === key.tab && event.shiftKey) {
    event.preventDefault();
    if (refLastFocusable) {
      refFocus(refLastFocusable);
    }
  }

  if (event.key === key.esc) {
    setEscKeyPressed(true);
    showOverlay(false);
  }
}
/**
 * This function will decode the JWT token.
 * @param token
 * @returns
 */
export function decodeJWT(token: string) {
  try {
    return JSON.parse(window.atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
}

/**
 * This function will return the user is Adult or not.
 * By readding the AGG values in decode JWT token.
 * @returns Boolean
 */
export function isAdult() {
  const hasJWT = Cookies.get(constants.bearerToken);
  const decodedJWT = decodeJWT(hasJWT ?? '');
  return decodedJWT?.agg === constants.adult;
}

/**
 * This function will remove all the list of cookies mentioned in the constants.
 * @param domain
 * @param path
 */
export function removeCookies(domain: string, path: string) {
  cookiesList.forEach((value: string) => {
    document.cookie = `${value}=; expires=${new Date(0).toUTCString()}; domain=${domain}; path=${path}`;
  });
}

/**
 * This function will remove the list of localStorage mentioned in the constants.
 */
function clearLocalStorage() {
  localStorageList.forEach((value) => {
    localStorage.removeItem(value);
  });
}
/**
 * This function will create the logout URL.
 * @returns
 */

function buildMsaLogoutUrl(maintainPage: boolean = false) {
  let msaOAuthUrl: URL | undefined;
  let xboxMSAClientId: string | undefined;
  let redirect_uri: URL | undefined;
  if (window.sharedEnvConfig) {
    msaOAuthUrl = new URL(window.sharedEnvConfig['endpoint.msaOAuthUrl']);
    const xboxOAuthUrl = window.sharedEnvConfig['endpoint.xboxOAuthUrl'];
    redirect_uri = generateRedirectURL(xboxOAuthUrl, maintainPage);
  }
  if (window.msaUtilsConfig) {
    xboxMSAClientId = window.msaUtilsConfig['xboxMSAClientId'];
  }
  msaOAuthUrl.searchParams.set('client_id', xboxMSAClientId);
  msaOAuthUrl.searchParams.set('redirect_uri', redirect_uri.toString());
  return msaOAuthUrl.toString();
}
/**
 * This function will create the redirect URL which will be used in the logout URL.
 * @param xboxOAuthUrl
 * @returns
 */
function generateRedirectURL(xboxOAuthUrl: string, maintainPage: boolean = false): URL {
  const envName = getEnvironmentParam();
  let ru: URL | undefined;
  if (window.sharedEnvConfig) {
    if (maintainPage) {
      ru = new URL(window.location.href);
    } else {
      ru = new URL(window.sharedEnvConfig['url.xboxLogoutReturnUrl']);
    }
    if (envName) {
      ru.searchParams.set('env', envName);
    }
  } else {
    const pathArray = window.location.pathname.split('/');
    ru = new URL(window.location.origin + '/' + pathArray[1]);
  }
  const redirectUrl = new URL(`${xboxOAuthUrl}signout`);
  redirectUrl.searchParams.set('state', 'logout');
  redirectUrl.searchParams.set('ru', ru.toString());
  return redirectUrl;
}
/**
 * This will add the environment params if it's available.
 * @param search
 * @returns string
 */
function getEnvironmentParam() {
  if (window.msaUtilsConfig && window.msaUtilsConfig['environmentName'] !== 'undefined' && window.msaUtilsConfig['environmentName'] !== '') {
    return window.msaUtilsConfig['environmentName'];
  }

  return '';
}

/**
 * This function will logout the current user.
 */
export function logout(maintainPage = false) {
  window.sessionStorage.clear();
  removeCookies(sharedEnvConfig['cookie.options'].domain, '/');
  clearLocalStorage();
  session.removeToken();
  const logoutUrl = buildMsaLogoutUrl(maintainPage);
  window.location.href = logoutUrl;
}
/**
 * This will help to get the value of query params from the URL.
 * @param name
 * @returns string
 */
export function getQueryParams(name: string) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(name);
}
/**
 * This function will remove a property from the given object.
 * @param obj
 * @param objKey
 * @returns obj
 */
export function removeProperty(obj: any, objKey: string) {
  delete obj[objKey];
  return obj;
}
/**
 * This will load all the images for toast message. To avoid the performance delay.
 */
export function preloadToastImages() {
  Object.values(toastIcons).forEach((icon) => {
    new Image().src = icon;
  });
}

/**
 * This function will check whether the user has purchased a java game from the entitlement api response.
 * @param response
 * @returns boolean
 */
export const javaGamePurchaseValidation = (response: ApiEntitlementResponse) => response.some(({ name }: ApiEntitlementItem) => name === javaEntitlementKey);

export function legacyRealmState(paymentIsActive: boolean, daysLeft: number) {
  return paymentIsActive === false && daysLeft <= 0 ? RealmStatus.Inactive : RealmStatus.Active;
}

/**
 * This function will replace the wildcard value with actual price value.
 * @param priceDescription
 * @param price
 * @returns string
 */
export function replaceWildCardTextWithPrice(priceDescription: string, price: string, currencyCode: string) {
  if (price) {
    const formattedPrice = currencyCode ? formatPrice(parseFloat(price), currencyCode, locale) : price;
    return priceDescription?.replace('%price%', formattedPrice);
  }
  return '';
}

export const isFlightAvailable = (flight: string) => {
  return localStorage.getItem(flights)?.split(' ').indexOf(flight) > -1;
};
export const getCv = () => {
  const cv = new Cv();
  return cv.getValue();
};
export const updateFlightInStorage = (flight: string, addFlight: boolean = true) => {
  //checking if flights is added already in local storage
  const localStorageFlights = localStorage.getItem(flights);
  if (localStorageFlights) {
    let flightsArr = localStorageFlights.split(' ');
    let flightIndex = flightsArr.indexOf(flight);

    if (addFlight && flightIndex === -1) {
      //add flight in local storage
      flightsArr.push(flight);
      localStorage.setItem(flights, flightsArr.join(' '));
    } else if (!addFlight && flightIndex != -1) {
      //remove flight from storage
      if (flightsArr.length > 1) {
        //remove specific flight if local storage flights has multiple items added
        flightsArr.splice(flightIndex, 1);
        localStorage.setItem(flights, flightsArr.join(' '));
      } else {
        //remove complete item from storage as local storage doesn't have any other flight set
        localStorage.removeItem(flights);
      }
    }
  } else if (addFlight) {
    //if flights is not there in local storage and addFlights flag is on then set item in storage
    localStorage.setItem(flights, flight);
  }
};

export function getMCToken() {
  let result = '';
  try {
    const mcTokenValue = localStorage.getItem('MCToken');
    const playFabXBLToken = localStorage.getItem('PlayFabXBLToken');
    if (mcTokenValue !== null && playFabXBLToken !== null) {
      const { mcToken, playFabXBL } = JSON.parse(mcTokenValue);
      if (playFabXBLToken === playFabXBL) {
        result = mcToken;
      }
    }
  } catch (e) {
    console.error(e);
  }

  return result;
}

export function getMarketplaceXBLToken() {
  return localStorage.getItem('MarketplaceXBLToken');
}

export function getXUIDToken() {
  return localStorage.getItem('XBLXid');
}

/**
 * This function will return the realm is trail or not
 * @param friendlyId
 * @returns boolean
 */
export function isTrialRealm(friendlyId: string) {
  return friendlyId?.toLowerCase().search(TRIAL_REALM) > -1;
}

/**
 * This function will return the price is available or not
 * @param price
 * @returns boolean
 */
export function isPriceNotAvailable(price: string) {
  return price === '';
}

/**
 * Returns a data AEM content name string with the appropriate suffix based on the disabled state of the button
 * @param contentName - the data AEM content name
 * @param isButtonDisabled - whether or not the button is disabled
 * @returns
 */
export function getButtonDataAEMContentName(contentName: string, isButtonDisabled: boolean): string {
  return `${contentName}${isButtonDisabled ? '_disabled' : ''}`;
}

/**
 * Returns the given list in ascending order.
 * Numbers, string, nested object, custom sorting can be performed.
 * Nested object can be performed with the key values passed into an array.
 * If in case a nested object contains array. Then the specific index needs to be added next to the key value.
 * @param list - list needs to be sorted.
 * @param isNumber - List is only a number.
 * @param keys - The key values passed into an array.
 * @returns sorted list.
 */
export function arrangeInAscending({ list, isNumber, customFunc, keys }: { list: any[]; isNumber?: boolean; customFunc?: Function; keys?: string[] }) {
  const extractKeyValue = (listObj: any) => {
    if (keys) {
      let extractedValue;
      for (let listKey of keys) {
        if (extractedValue) {
          extractedValue = extractedValue[listKey];
        } else {
          extractedValue = listObj[listKey];
        }
      }
      return extractedValue?.toLocaleLowerCase();
    }
  };
  return list.sort((a, b) => {
    if (keys) {
      return extractKeyValue(a) < extractKeyValue(b) ? -1 : 1;
    }
    if (customFunc) {
      return customFunc(a, b);
    } else if (isNumber) {
      return a - b;
    } else {
      return a < b ? -1 : 1;
    }
  });
}

/**
 * Returns a templated string
 * @param str - the string which has template added
 * @param ...arr - multiple strings to update str with appropriate values
 * @returns
 */
export function updateStringWithTemplate(str: string, ...arr: Array<string>): string {
  return str?.replace(/\[(\d+)\]/g, function (_, i) {
    return arr[--i];
  });
}
interface btnProps {
  role: ButtonRole;
  theme: Exclude<MinecraftTheme, MinecraftTheme.Black>;
  subTheme: ButtonSubTheme;
}
export const mapColorToButtonProps = (btnColor: ButtonColor): btnProps => {
  switch (btnColor) {
    case 'purple-button':
      return {
        role: ButtonRole.Hero,
        theme: MinecraftTheme.Realms,
        subTheme: ButtonSubTheme.Primary
      };
    case 'red-button':
      return {
        role: ButtonRole.Primary,
        theme: MinecraftTheme.Vanilla,
        subTheme: ButtonSubTheme.Destructive
      };
    case 'green-button':
      return {
        role: ButtonRole.Hero,
        theme: MinecraftTheme.Vanilla,
        subTheme: ButtonSubTheme.Primary
      };
    case 'grey-button':
      return {
        role: ButtonRole.Hero,
        theme: MinecraftTheme.Vanilla,
        subTheme: ButtonSubTheme.Secondary
      };
  }
};

export async function isFlightEnabled(flightName: NetServiceTypes.FlightName): Promise<boolean> {
  return await NetService.isFlightEnabled(flightName, environment['endpoint.net.minecraftServices']);
}

export const makeWindowListener = (handlers: IPurchaseEventHandlers) => (event: WindowEventMap['message']) => {
  const msg = event.data as IPurchasePostMessage;
  switch (msg.message) {
    case 'done':
      handlers.done(msg);
      break;
    case 'stageChanged':
      handlers.stageChanged?.(msg);
      break;
    case 'status':
      handlers.status?.(msg);
      break;
    case 'sizeChanged':
      handlers.sizeChanged?.(msg);
      break;
    case 'pageMessage':
      handlers.pageMessage?.(msg);
      break;
  }
};

export const getTokenUserLocation = (): string | null => {
  try {
    const token = JSON.parse(localStorage.getItem('MCToken')).mcToken.split(' ')[1];
    return parseJwt(token).ugeo?.toLowerCase();
  } catch {
    return null;
  }
};
